import * as React from 'react';
import { AuthState } from '../auth/auth.types';
import { AdminState } from '../admin/admin.types';
import {
  PosState,
  GetPosHistoriesRequest,
  PosRegistrationResponse,
  RefundPosRequest
} from './Types';
import SelectField from 'material-ui/SelectField';
import MenuItem from 'material-ui/MenuItem';
import { isAdminAccount, isImpersonating } from '../admin/Util';
import { isPosDisabled } from '../util/permission.util';
import RaisedButton from 'material-ui/RaisedButton';
import Dialog from 'material-ui/Dialog';
import FlatButton from 'material-ui/FlatButton';
import RefreshIndicator from 'material-ui/RefreshIndicator';
import { mmsStore } from '../app/app.config';
import { ROW_CLICKED } from './Constants';
import { isAuthTokenExpired } from '../commons/AuthTokenValidator';
import ReactTable from 'react-table';
import { FormattedNumber } from 'react-intl';
import { dateFromString } from '../util/date-util';
import { overlayStyle, renderOverlayLoading } from '../util/Overlay';
import CommonFeatures from '../commons/CommonFeatures';
import { BusinessState } from 'src/business/business.types';

type TableData = {
  readonly receiptId: string
  readonly description: string
  readonly amount: JSX.Element
  readonly transactionType: string
  readonly transactionDate: string
  readonly refund: JSX.Element
  readonly viewDetail: JSX.Element
};

type Props = {
  readonly authState: AuthState
  readonly adminState: AdminState
  readonly posState: PosState
  readonly businessState: BusinessState
  readonly toAdminDashboard: () => void
  readonly toDashboard: () => void
  readonly getPosHistories: (req: GetPosHistoriesRequest) => Promise<Response>
  readonly refund: (req: RefundPosRequest) => Promise<Response>
  readonly toPosSaleResult: () => void
};

type States = {
  readonly year: number
  readonly currentYear: number
  readonly month: number
  readonly confirmationModalOpen: boolean
  readonly saleToRefund: RefundPosRequest
  readonly histories: PosRegistrationResponse[]
  readonly loading: boolean
  readonly loadedHistories: boolean
  readonly isApiCalledAlready: boolean
  readonly loadedNewHistories: boolean
  readonly transactionError: string
};

class PosHistoryComponent extends React.Component<Props, States> {

  constructor(props: Props) {
    super(props);

    this.state = {
      year: new Date().getFullYear(),
      currentYear: new Date().getFullYear(),
      month: new Date().getMonth() + 1,
      confirmationModalOpen: false,
      saleToRefund: {receiptId: ''},
      histories: [],
      loading: false,
      loadedHistories: false,
      isApiCalledAlready: false,
      loadedNewHistories: true,
      transactionError: ''
    };
  }

  componentDidMount() {
    const { authState, getPosHistories, toAdminDashboard, adminState, toDashboard } = this.props;
    const { year, month } = this.state;

    if (isAdminAccount(authState) && !isImpersonating(adminState)) {
      return toAdminDashboard();
    }

    if (!isPosDisabled(authState, adminState)) {
      this.setState(
        {isApiCalledAlready: true},
        () => {
          getPosHistories({year: year, month: month})
          .then(response => {
            switch (this.props.posState.statusCode) {
              case (200):
                return this.setState(
                  {histories: this.props.posState.histories}
                );
              case (403):
                return toDashboard();
              default:
                return;
            }
          });
        }
      );
    }
  }

  componentWillUnmount() {
    this.setState({loading: false, loadedHistories: false});
  }

  componentWillReceiveProps(nextProps: Props) {
    if (
      this.props !== nextProps &&
      !isAuthTokenExpired() &&
      nextProps.authState.statusCode === 200 &&
      !this.state.loadedHistories &&
      !this.state.isApiCalledAlready
    ) {

      nextProps.getPosHistories({year: this.state.year, month: this.state.month})
      .then((response: Response) => {
        switch (this.props.posState.statusCode) {
          case 200:
            return this.setState({loadedHistories: true, loading: false});
          default:
            return;
        }
      });
    } else {
      this.setState({histories: nextProps.posState.histories, loadedHistories: true});
    }
  }

  render() {
    const { year, currentYear, month } = this.state;

    return(
      <CommonFeatures
        authState={this.props.authState}
        businessState={this.props.businessState}
      >
        <div className="page-header-row row mt-2 mb-2">
          <div className="col-auto">
            <h3>P.O.S. History</h3>
          </div>
        </div>
        <div className="row">
          <div className="col-auto">
            <SelectField
              floatingLabelText="Year"
              value={year}
              onChange={this.handleYearFieldChange}
            >
              <MenuItem value={currentYear + 1} primaryText={currentYear + 1} />
              <MenuItem value={currentYear} primaryText={currentYear} />
              <MenuItem value={currentYear - 1} primaryText={currentYear - 1} />
              <MenuItem value={currentYear - 2} primaryText={currentYear - 2} />
              <MenuItem value={currentYear - 3} primaryText={currentYear - 3} />
            </SelectField>
          </div>
          <div className="col-auto">
            <SelectField
              floatingLabelText="Month"
              value={month}
              onChange={this.handleMonthFieldChange}
            >
              <MenuItem value={1} primaryText="January" />
              <MenuItem value={2} primaryText="February" />
              <MenuItem value={3} primaryText="March" />
              <MenuItem value={4} primaryText="April" />
              <MenuItem value={5} primaryText="May" />
              <MenuItem value={6} primaryText="June" />
              <MenuItem value={7} primaryText="July" />
              <MenuItem value={8} primaryText="August" />
              <MenuItem value={9} primaryText="September" />
              <MenuItem value={10} primaryText="October" />
              <MenuItem value={11} primaryText="November" />
              <MenuItem value={12} primaryText="December" />
            </SelectField>
          </div>
        </div>
        <div className="row mb-4 mt-4 animated fadeIn">
          <div
            className="col-12"
            style={overlayStyle(this.state.loadedNewHistories)}
          >
            {renderOverlayLoading(this.state.loadedNewHistories, '25%')}
            {this.renderPosHistoryTable()}
          </div>
        </div>
        {this.renderRefundConfirmationDialog()}
      </CommonFeatures>
    );
  }

  private createTableData = (p: PosRegistrationResponse): TableData => {
    return({
      receiptId: p.receiptId,
      description: p.description,
      amount: <FormattedNumber value={p.total} style="currency" currency="USD" />,
      transactionType: p.transactionType,
      transactionDate: p.transDate,
      refund: this.renderRefundButton(p.refundable, p.receiptId),
      viewDetail: this.renderViewDetailButton(p)
    });
  }

  private renderPosHistoryTable = (): JSX.Element => {
    return(
      <ReactTable
        data={this.state.histories.map((p: PosRegistrationResponse) => this.createTableData(p))}
        columns={[
          {
            accessor: 'receiptId',
            Header: 'Receipt ID',
            style: {textAlign: 'center'}
          },
          {                                                                                       
            accessor: 'description',
            Header: 'Description',
            style: {textAlign: 'center'}
          },
          {
            accessor: 'amount',
            Header: 'Amount',
            style: {textAlign: 'center'},
            sortMethod: (a, b, desc) => {
              if (a.props.value > b.props.value) {
                return 1;
              } else if (a.props.value < b.props.value) {
                return -1;
              } else {
                return 0;
              }
            }
          },
          {
            accessor: 'transactionType',
            Header: 'Transaction Type',
            style: {textAlign: 'center'}
          },
          {
            accessor: 'transactionDate',
            Header: 'Transaction Date',
            style: {textAlign: 'center'},
            sortMethod: (a, b, desc) => {
              if (dateFromString(a) > dateFromString(b)) {
                return 1;
              } else if (dateFromString(a) < dateFromString(b)) {
                return -1;
              } else {
                return 0;
              }
            }
          },
          {
            accessor: 'refund',
            Header: 'Refund',
            style: {textAlign: 'center'}
          },
          {
            accessor: 'viewDetail',
            Header: 'View Detail',
            style: {textAlign: 'center'}
          }
        ]}
        defaultPageSize={20}
        className="-striped -highlight"
        keyField={'receiptId'}
      />
    );
  }

  private renderViewDetailButton = (p: PosRegistrationResponse): JSX.Element => {
    return(
      <RaisedButton
        label="Detail"
        primary={true}
        onClick={() => this.handleDetailClick(p)}
      />
    );
  }

  private handleDetailClick = (p: PosRegistrationResponse): void => {
    const selected = this.state.histories
      .filter((pos: PosRegistrationResponse, index: number) =>
        pos.receiptId === p.receiptId && pos.transactionType === p.transactionType
      );

    mmsStore.dispatch(
      {
        type: ROW_CLICKED,
        payload: {
          body: {
            entity: {...(selected.length === 1 ? selected[0] : undefined)}
          }
        }
      }
    );

    this.props.toPosSaleResult();
  }

  private renderCancelButton = (onClickFunction: (e: React.SyntheticEvent<{}>) => void): JSX.Element => {
    return(
      <FlatButton
        label="Cancel"
        secondary={true}
        onClick={onClickFunction}
        disabled={this.state.loading}
      />
    );
  }

  private renderSubmitButton = (onClickFunction: (e: React.SyntheticEvent<{}>) => void): JSX.Element => {
    if (this.state.loading) {
      return(
        <RefreshIndicator
          size={40}
          left={10}
          top={0}
          status="loading"
          style={{
            display: 'inline-block',
            position: 'relative',
            float: 'right',
            marginRight: '3rem'
          }}
        />
      );
    } else {
      return(
        <FlatButton
          label="Submit"
          primary={true}
          onClick={onClickFunction}
        />
      );
    }
  }

  private closeConfirmationModal = (): void => this.setState({confirmationModalOpen: false});

  private renderRefundConfirmationDialog = (): JSX.Element => {
    return(
      <Dialog
        title="Confirm Refund"
        actions={[
          this.renderCancelButton(this.closeConfirmationModal),
          this.renderSubmitButton(() => {
            this.setState(
              {loading: !isAuthTokenExpired()},
              () => this.refund(this.state.saleToRefund)
            );
          })
        ]}
        modal={true}
        open={this.state.confirmationModalOpen}
        onRequestClose={() => this.setState({confirmationModalOpen: false})}
      >
        Do you want to refund this transaction?
        <div className="red">
          {this.state.transactionError}
        </div>
      </Dialog>
    );
  }

  private renderRefundButton = (refundable: boolean, id: string): JSX.Element => {
    return(
      <RaisedButton
        label="Refund"
        primary={true}
        onClick={() => this.setState({confirmationModalOpen: true, saleToRefund: {receiptId: id}})}
        disabled={!refundable}
      />
    );
  }

  private refund = (req: RefundPosRequest): void => {
    this.props.refund(req)
    .then(response => {
      switch (this.props.posState.statusCode) {
        case 200:
          this.checkResponseCodeAndRefresh();
          break;
        case 400:
          this.setState({
            transactionError: 'Transaction is DECLINED',
            loading: false
          });
          break;
        default:
          break;
      }
    });
  }

  private checkResponseCodeAndRefresh = (): void => {
    const { year, month } = this.state;
    const { posState } = this.props;

    // responseCoee bigger than 30 is error. Approved is '027'.
    if (parseInt(posState.sale.responseCode, 10) > 30) {
      return this.setState({
        transactionError: 'Transaction is DECLINED',
        loading: false
      });
    } else {
      this.props.getPosHistories({year: year, month: month})
      .then(resposne => 
        this.setState({
          transactionError: '',
          confirmationModalOpen: false, 
          loading: false
        })
      );
    }
  }

  private handleYearFieldChange = (event: React.SyntheticEvent<{}>, index: number, value: number) => {
    const { getPosHistories } = this.props;

    if (isAuthTokenExpired()) {
      getPosHistories({year: this.state.year, month: this.state.month});
    } else {
      this.setState(
        {year: value, loadedNewHistories: false},
        () => {
          getPosHistories({year: this.state.year, month: this.state.month})
          .then(res => {
            switch (this.props.posState.statusCode) {
              case 200:
                this.setState({loadedNewHistories: true});
                break;
              default:
                break;
            }
          });
        });
    }
  }

  private handleMonthFieldChange = (event: React.SyntheticEvent<{}>, index: number, value: number) => {
    const { getPosHistories } = this.props;

    if (isAuthTokenExpired()) {
      getPosHistories({year: this.state.year, month: this.state.month});
    } else {
      this.setState(
        {month: value, loadedNewHistories: false}, 
        () => {
          getPosHistories({year: this.state.year, month: this.state.month})
          .then(res => {
            switch (this.props.posState.statusCode) {
              case 200:
                this.setState({loadedNewHistories: true});
                break;
              default:
                break;
            }
          });
        }
      );
    }
  }
}

export default PosHistoryComponent;



// WEBPACK FOOTER //
// ./src/pos/PosHistoryComponent.tsx